import React, { useEffect, useState } from "react";
import { Loader2, Paperclip, ThumbsDown, ThumbsUp } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ProposalRecord } from "@/types/proposal";

/**
 * Records the customer's decision made outside the CRM (an email, a call, a signed paper copy), with a
 * note and optionally the file as proof. Online acceptance by the customer is always preferred.
 */
export function RecordDecisionDialog({
  proposal,
  open,
  onOpenChange,
  onDone,
}: {
  proposal: ProposalRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (p: ProposalRecord, advancedTo: string | null) => void;
}) {
  const { success, error } = useToast();
  const [decision, setDecision] = useState<"ACCEPTED" | "DECLINED">("ACCEPTED");
  const [signer, setSigner] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDecision("ACCEPTED");
    setSigner(proposal.contact?.name || "");
    setNote("");
    setFile(null);
  }, [open, proposal.id]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return error("Add a note", "Say how and when the customer decided");
    setBusy(true);
    try {
      let file_url: string | undefined;
      if (file) {
        const fd = new FormData();
        fd.append("deal_id", proposal.deal_id);
        fd.append("file", file);
        const up: any = await api.upload("/api/Upload/File", fd);
        file_url = up?.file_url;
      }
      const res = unwrapObject<{ advanced_to: string | null; proposal: ProposalRecord }>(
        await api.post(`/api/Proposal/RecordDecision/${proposal.id}`, {
          decision,
          note: note.trim(),
          signer_name: signer.trim() || undefined,
          file_url,
          file_name: file?.name,
        })
      );
      success(
        decision === "ACCEPTED" ? "Acceptance recorded" : "Decline recorded",
        res.advanced_to ? `The deal moved to ${res.advanced_to}.` : `${proposal.display_number} is now ${decision === "ACCEPTED" ? "accepted" : "declined"}.`
      );
      onOpenChange(false);
      onDone(res.proposal, res.advanced_to);
    } catch (err: any) {
      error("Not recorded", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record the customer's decision</DialogTitle>
          <DialogDescription>
            For a decision made outside the CRM, by email, phone or a signed paper copy. If you can, ask the customer to accept through their
            link instead: that records their signature and evidence.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                ["ACCEPTED", "Accepted", ThumbsUp, "border-success bg-success/10 text-success"],
                ["DECLINED", "Declined", ThumbsDown, "border-destructive bg-destructive/10 text-destructive"],
              ] as const
            ).map(([id, label, Icon, active]) => (
              <button
                key={id}
                type="button"
                onClick={() => setDecision(id)}
                className={cn("h-10 rounded-lg border text-sm font-semibold inline-flex items-center justify-center gap-1.5", decision === id ? active : "hover:bg-muted")}
              >
                <Icon className="size-4" /> {label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Who decided</Label>
            <Input value={signer} maxLength={120} onChange={(e) => setSigner(e.target.value)} placeholder="Name and role on the customer's side" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{decision === "ACCEPTED" ? "How and when they accepted *" : "Why they declined *"}</Label>
            <Textarea
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={decision === "ACCEPTED" ? "e.g. Email from the CFO on 8 Oct confirming we can go ahead" : "e.g. Budget frozen until next year"}
            />
          </div>
          {decision === "ACCEPTED" && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground rounded-md bg-muted/40 px-3 py-2 cursor-pointer">
              <Paperclip className="size-4" />
              <span className="flex-1 truncate">{file ? file.name : "Attach the email or signed copy (optional)"}</span>
              <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.eml,.msg,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />}
              Record {decision === "ACCEPTED" ? "acceptance" : "decline"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
